import type { PersonaId } from "@petci/domain";
import { HOME_CONTEXT_PER_PERSONA } from "./home-context";

export type HomeCardId =
  | "balance"
  | "context"
  | "insights"
  | "ai-explainer"
  | "hurricane"
  | "outage-report"
  | "anomaly-hero"
  | "hotel-overview"
  | "usage"
  | "account"
  | "hotel-ecor"
  | "peak-demand"
  | "active-requests";

export interface HomeCardEntry {
  id: HomeCardId;
  visible: boolean;
}

export interface HomeCardFlags {
  hurricaneSeason: boolean;
  hasAnomaly: boolean;
  isHotel: boolean;
  /** This persona has a bill to explain. Gates `ai-explainer` (D-O3). */
  hasBill: boolean;
  /** This persona has billing history to chart. Gates `usage` (D-O3). */
  hasUsage: boolean;
}

interface CardSpec {
  id: HomeCardId;
  visible: (flags: HomeCardFlags, personaId: PersonaId) => boolean;
}

const always = () => true;

/**
 * D-O4 — DELIBERATE divergences from web's `home-card-order.ts`, documented
 * per the cross-surface parity contract ("divergence permitted only when
 * documented as deliberate"). Both are consequences of mobile's chrome, not
 * drift:
 *
 *  1. `hurricane` sits HIGH here (#2) and LOW on web (#5-ish). Web renders a
 *     `HurricaneBanner` above every authenticated route, so there the banner
 *     owns storm AWARENESS and the card is only the advice doorway. Mobile has
 *     no banner — this card IS the awareness surface, so it must lead.
 *  2. Mobile carries no `network-status`, `activity` or `identity` card. The
 *     island-wide outage pill (chrome) does the first job, Payment History
 *     (F-C2) the second, and the `account` card the third.
 *  3. For P3 the AI explainer sits AFTER `insights`/`hotel-overview`, where web
 *     puts its `ai-callout` BEFORE both (and the two surfaces also invert
 *     `insights` vs `hotel-overview`). Deliberate, per surface: a phone's first
 *     screen belongs to the operational cards, so the offer to explain the bill
 *     follows them, while web's wider workspace can afford to place the offer
 *     directly after the peak-demand alert it explains. Added 2026-07-29
 *     closing observation O-1 from the R-A/home-order CHECK.
 *
 * Everything else is expected to MATCH web, and where the two lists disagree
 * without an entry here, that is the defect — D-O2 was exactly that: the two
 * surfaces inverted `peak-demand` vs `hotel-overview` with no justification.
 */
const RESIDENTIAL_PRIORITY: CardSpec[] = [
  { id: "balance", visible: always },
  { id: "hurricane", visible: (f) => f.hurricaneSeason },
  // D-O3: no bill, no bill explainer. P5 (internal CSR) has `bills: []`, and
  // was being offered "Confused about your bill?" for a bill that does not
  // exist — the H0.7 honesty class, and the same suppression web gets by
  // omitting the card from P5's contract.
  { id: "ai-explainer", visible: (f) => f.hasBill },
  { id: "insights", visible: always },
  { id: "context", visible: (_f, pid) => HOME_CONTEXT_PER_PERSONA[pid] != null },
  { id: "outage-report", visible: always },
  // D-O3: a 12-month chart drawn from zero readings is a flat line pretending
  // to be data.
  { id: "usage", visible: (f) => f.hasUsage },
  { id: "active-requests", visible: always },
  { id: "account", visible: always },
];

const HOTEL_PRIORITY: CardSpec[] = [
  { id: "balance", visible: always },
  { id: "hurricane", visible: (f) => f.hurricaneSeason },
  // D-O2: the demand ALERT precedes the ambient portfolio rollup. 412 of 450 kW
  // is actionable and has demand charges attached; the overview is a summary
  // that will still be there afterwards. Web has always ordered it this way —
  // the two surfaces were inverting the same pair with nothing to justify it.
  { id: "peak-demand", visible: (f) => f.isHotel },
  { id: "hotel-overview", visible: (f) => f.isHotel },
  { id: "insights", visible: always },
  { id: "ai-explainer", visible: (f) => f.hasBill },
  { id: "hotel-ecor", visible: (f) => f.isHotel },
  { id: "usage", visible: (f) => f.hasUsage },
  { id: "outage-report", visible: always },
  { id: "active-requests", visible: always },
  { id: "account", visible: always },
];

const PM_PRIORITY: CardSpec[] = [
  { id: "anomaly-hero", visible: (f) => f.hasAnomaly },
  { id: "hurricane", visible: (f) => f.hurricaneSeason },
  { id: "balance", visible: always },
  { id: "ai-explainer", visible: (f) => f.hasBill },
  { id: "insights", visible: always },
  { id: "outage-report", visible: always },
  { id: "usage", visible: (f) => f.hasUsage },
  { id: "active-requests", visible: always },
  { id: "account", visible: always },
];

const PRIORITY_BY_PERSONA: Record<PersonaId, CardSpec[]> = {
  P1: RESIDENTIAL_PRIORITY,
  P2: RESIDENTIAL_PRIORITY,
  P3: HOTEL_PRIORITY,
  P4: PM_PRIORITY,
  P5: RESIDENTIAL_PRIORITY,
};

export function homeCardPriority(
  personaId: PersonaId,
  flags: HomeCardFlags,
): HomeCardEntry[] {
  const specs = PRIORITY_BY_PERSONA[personaId];
  return specs.map((spec) => ({
    id: spec.id,
    visible: spec.visible(flags, personaId),
  }));
}
